/**
 * Helpers para analytics - Mapeo de rutas a módulos y secciones
 */

import { ANALYTICS_MODULES, ANALYTICS_SECTIONS } from '../../constants/analyticsConstants';

/**
 * Genera el módulo de cotización específico para un producto
 * @param {string} producto - Nombre del producto (ej: 'automotor', 'hogar', 'notebook', 'motovehículo')
 * @returns {string} Módulo de analytics (ej: 'cotizacion_automotor', 'cotizacion_hogar')
 */
export const getQuotationModule = (producto) => {
  if (!producto || typeof producto !== 'string') {
    return ANALYTICS_MODULES.COTIZACION; // Fallback al módulo genérico
  }
  
  // Normalizar: motovehículo -> motovehiculo
  const normalizedProduct = producto === 'motovehículo' ? 'motovehiculo' : producto;
  
  return `cotizacion_${normalizedProduct}`;
};

/**
 * Mapea una ruta (pathname) a un módulo de analytics
 * Basado en lógica de negocio, no en estructura de carpetas
 * 
 * @param {string} pathname - Ruta actual (ej: '/u/dashboard', '/u/cotizar/automotor')
 * @returns {string} Módulo de analytics o null si no se puede determinar
 */
export const getModuleFromPath = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return null;

  const path = pathname.toLowerCase();
  
  // Rutas de Master (empiezan con /m)
/*   if (path.includes('/')) {
    return ANALYTICS_MODULES.LANDING;
  } */
  if (path.includes('/m/operaciones')) {
    return ANALYTICS_MODULES.MASTER_OPERACIONES;
  }
  if (path.includes('/m/canaldigital')) {
    return ANALYTICS_MODULES.MASTER_DIGITAL_CHANNEL;
  }

  
  // Rutas de Polkistas (empiezan con /u)
  if (path.includes('/dashboard')) {
    return ANALYTICS_MODULES.DASHBOARD;
  }
  
  // Rutas de cotización - discriminar por producto
  if (path.includes('/cotizar')) {
    // Productos disponibles en la aplicación
    const products = [
      'automotor', 
      'hogar', 
      'asistenciaviajero', 
      'motovehiculo', 
      'notebook', 
      'tablet', 
      'monopatin', 
      'bicicleta', 
      'robosvarios'
    ];
    for (const product of products) {
      if (path.includes(`/cotizar/${product}`)) {
        // Normalizar: motovehículo -> motovehiculo
        const normalizedProduct = product === 'motovehículo' ? 'motovehiculo' : product;
        return `cotizacion_${normalizedProduct}`;
      }
    }
    // Si no se encuentra un producto específico, usar el módulo genérico
    return ANALYTICS_MODULES.COTIZACION;
  }
  
  // Rutas públicas
  if (path === '/' || path === '/landing') {
    return ANALYTICS_MODULES.LANDING;
  }
  if (path.includes('/productos')) {
    return ANALYTICS_MODULES.PRODUCTOS;
  }
  
  return null;
};

/**
 * Valida que un módulo sea válido (existe en las constantes o sigue el patrón dinámico)
 * Solo en desarrollo
 * 
 * @param {string} module - Módulo a validar
 * @returns {boolean} true si es válido
 */
export const isValidModule = (module) => {
  if (process.env.NODE_ENV !== 'development') return true;
  
  // Validar módulos estáticos
  if (Object.values(ANALYTICS_MODULES).includes(module)) {
    return true;
  }
  
  // Validar módulos dinámicos: cotizacion_{producto}
  const products = [
    'automotor', 
    'hogar', 
    'asistenciaviajero', 
    'motovehiculo', 
    'notebook', 
    'tablet', 
    'monopatin', 
    'bicicleta', 
    'robosvarios'
  ];
  if (module.startsWith('cotizacion_')) {
    const product = module.replace(/^cotizacion_/, '');
    return products.includes(product);
  }
  
  return false;
};

/**
 * Valida que una sección sea válida (existe en las constantes)
 * Solo en desarrollo
 * 
 * @param {string} section - Sección a validar
 * @returns {boolean} true si es válida
 */
export const isValidSection = (section) => {
  if (process.env.NODE_ENV !== 'development') return true;
  return Object.values(ANALYTICS_SECTIONS).includes(section);
};

